'use client';

import { NumberTooltip } from '@streetferret/ui-web/number-tooltip';
import { cn, nFormatter } from '@streetferret/utils';
import { Button } from '@streetferret/ui-web/button';
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@streetferret/ui-web/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@streetferret/ui-web/dialog';
import { Separator } from '@streetferret/ui-web/separator';
import { motion } from 'framer-motion';
import Fuse from 'fuse.js';
import { Expand } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';

export type BarDatum = {
  beforeBar?: () => ReactNode;
  afterBar?: () => ReactNode;
  title: string;
  value: number;
  highlight?: boolean;
};
export function BarsList({
  data,
  total,
  title,
  description,
  limit = 10,
  unit,
  cardClassName,
}: {
  data: BarDatum[];
  total: number;
  title: string;
  description: string;
  limit?: number;
  unit: string;
  cardClassName?: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const closeModal = () => setIsOpen(false);
  const openModal = () => setIsOpen(true);

  const valueFormatter = (number: number) => {
    return `${nFormatter(number)} ${unit}`;
  };
  return (
    <Card className={cn('overflow-hidden !rounded-[28px] p-0', cardClassName)}>
      <CardHeader className="px-6 pb-4 pt-6">
        <CardTitle className="font-display text-2xl font-black">
          {title}
        </CardTitle>
        <CardDescription className="text-foreground/70">
          {description}
        </CardDescription>
      </CardHeader>
      <Separator className="" />
      <div className="relative overflow-hidden px-6 pt-5">
        <Bars
          limit={limit}
          data={data}
          valueFormatter={valueFormatter}
          total={total}
        />
        {data.length > limit ? (
          <div className="sticky inset-x-0 bottom-0 z-20 bg-gradient-to-t from-background to-transparent">
            <div className="-mt-2.5 flex w-full items-center justify-center bg-gradient-to-t from-background to-transparent">
              <Button
                onClick={openModal}
                variant={'outline'}
                size={'sm'}
                className="bg-background">
                <Expand className="mr-1 h-4 w-4" />
                View All
              </Button>
            </div>
            <Dialog open={isOpen} onOpenChange={setIsOpen}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{title}</DialogTitle>
                </DialogHeader>
                <Separator />

                <div className="-mt-3.5">
                  <div className="relative h-[450px] overflow-x-hidden overflow-y-scroll pt-2">
                    <Bars
                      data={data}
                      valueFormatter={valueFormatter}
                      total={total}
                    />
                    <div className="sticky inset-x-0 bottom-0 h-20 bg-gradient-to-t from-background to-transparent"></div>
                  </div>
                  <Button
                    className="w-full"
                    variant={'outline'}
                    onClick={closeModal}>
                    Close
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        ) : (
          <div className="h-5"></div>
        )}
      </div>
    </Card>
  );
}

export function Bars({
  limit,
  data,
  total,
  valueFormatter,
  search,
}: {
  limit?: number;
  data: BarDatum[];
  valueFormatter: (number: number) => string;
  total: number;
  search?: string;
}) {
  const fuse = useMemo(() => {
    if (limit) {
      return null;
    }
    return new Fuse(data, {
      keys: ['title'],
    });
  }, [limit, data]);

  const filteredData = useMemo(() => {
    if (limit) {
      return data.slice(0, limit);
    } else {
      return search ? fuse!.search(search).map(r => r.item) : data;
    }
  }, [data, limit, search, fuse]);
  return (
    <div className="grid gap-4">
      {filteredData.map(
        ({ title, value, beforeBar, afterBar, highlight }, idx) => (
          <div key={idx} className="grid">
            <div className="w-10 tabular-nums">{beforeBar?.()}</div>
            <div className="relative z-10 flex items-center justify-between">
              <div className="z-10 h-6 gap-2 !px-2">{title}</div>
              <motion.div
                style={{
                  width: `${(value / (total || 0)) * 100}%`,
                }}
                className={cn(
                  'absolute h-8 origin-left rounded-md bg-run/60',
                  highlight && 'border-run/90 shadow shadow-foreground/40',
                )}
                transition={{
                  ease: 'easeOut',
                  duration: 0.35 + idx * 0.01,
                  delay: 0.2 + idx * 0.04,
                }}
                initial={{ transform: 'scaleX(0)' }}
                animate={{ transform: 'scaleX(1)' }}
              />
              <NumberTooltip value={value}>
                <div className="z-10 mr-2 w-24 text-right font-bold tabular-nums">
                  {valueFormatter(value)}
                </div>
              </NumberTooltip>
            </div>
            <div className="flex items-center justify-center text-center">
              {afterBar?.()}
            </div>
          </div>
        ),
      )}
    </div>
  );
}
